import type { PageableResponse } from '../types'

const handleRequest = async <T>(promise: Promise<T>): Promise<[T | undefined, unknown]> => {
  try {
    const data = await promise
    return [data, undefined] as [T, undefined]
  }
  catch (error) {
    return await Promise.resolve([undefined, error] as [undefined, unknown])
  }
}
const { handleException } = useHandleException()

const wait = (delay: number) => new Promise(resolve => setTimeout(resolve, delay))

export const useFetchGenerator = <T>(delay: number = 500) => {
  async function* fetchGenerator(
    request: (page: number) => Promise<PageableResponse<T>>,
    page: number = 1,
  ): AsyncGenerator<PageableResponse<T>> {
    const [response, error] = await handleRequest<PageableResponse<T>>(request(page))
    if (error) {
      handleException(error)
    }

    if (!response) {
      return
    }

    yield response

    if (response.meta.lastPage <= response.meta.currentPage) {
      return
    }

    if (delay) {
      await wait(delay)
    }

    yield * fetchGenerator(request, response.meta.currentPage + 1)
  }

  return {
    fetchGenerator,
  }
}
